import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Typography,
  Button,
  TextField,
  IconButton,
  Box,
  useTheme,
} from '@mui/material';
import {
  AccountBalance as BankIcon,
  Person as PersonIcon,
  Receipt as ChequeIcon,
  CalendarToday as CalendarIcon,
  CurrencyRupee as AmountIcon,
  AccessTime as TimeIcon,
  Image as ImageIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  TextSnippet as TextSnippetIcon,
  Numbers as NumbersIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import ImageViewer from '../common/ImageViewer';

const ChequeDetails = ({ 
  chequeData, 
  canEdit = false, 
  onUpdate = null,
  payeeName = '',
  totalExgratiaAmount = 0,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    if (chequeData) {
      setEditData({
        cheque_number: chequeData.cheque_number || '',
        bank_name: chequeData.bank_name || '',
        bank_account_number: chequeData.bank_account_number || '',
        payee_name: chequeData.payee_name || '',
        cheque_amount: chequeData.cheque_amount || '',
        cheque_date: chequeData.cheque_date || '',
        remarks: chequeData.remarks || '',
      });
    } else {
      setEditData({
        cheque_number: '',
        bank_name: 'STATE BANK OF SIKKIM',
        bank_account_number: '',
        payee_name: payeeName || '',
        cheque_amount: totalExgratiaAmount || '',
        cheque_date: new Date().toISOString().split('T')[0],
        remarks: '',
      });
    }
  }, [chequeData, payeeName, totalExgratiaAmount]);

  const handleEditStart = () => {
    setIsEditing(true);
  };

  const handleEditSave = () => {
    if (!editData.cheque_number.trim()) {
      alert('Cheque number is required');
      return;
    }
    if (!editData.bank_name.trim()) {
      alert('Bank name is required');
      return;
    }
    if (!editData.bank_account_number.trim()) {
      alert('Bank account number is required');
      return;
    }
    if (!editData.payee_name.trim()) {
      alert('Payee name is required');
      return;
    }
    if (!editData.cheque_amount || parseFloat(editData.cheque_amount) <= 0) {
      alert('Valid amount is required');
      return;
    }
    if (!editData.cheque_date) {
      alert('Cheque date is required');
      return;
    }
    if (!chequeData && !selectedImage) {
      alert('Cheque image is required for new entries');
      return;
    }

    setIsEditing(false);
    if (onUpdate) {
      const submitData = new FormData();
      submitData.append('data', JSON.stringify(editData));
      if (selectedImage) {
        submitData.append('image', selectedImage);
      }
      onUpdate(submitData);
    }
  };

  const handleEditCancel = () => {
    if (chequeData) {
      setEditData({
        cheque_number: chequeData.cheque_number || '',
        bank_name: chequeData.bank_name || '',
        bank_account_number: chequeData.bank_account_number || '',
        payee_name: chequeData.payee_name || '',
        cheque_amount: chequeData.cheque_amount || '',
        cheque_date: chequeData.cheque_date || '',
        remarks: chequeData.remarks || '',
      });
    } else {
      setEditData({
        cheque_number: '',
        bank_name: 'STATE BANK OF SIKKIM',
        bank_account_number: '',
        payee_name: payeeName || '',
        cheque_amount: totalExgratiaAmount || '',
        cheque_date: new Date().toISOString().split('T')[0],
        remarks: '',
      });
    }
    setSelectedImage(null);
    setImagePreview(null);
    setIsEditing(false);
  };

  const handleInputChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageSelect = (file) => {
    setSelectedImage(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleImageSelect(file);
    }
  };

  const formatAmount = (amount) => {
    if (!amount) return '₹0';
    return `₹${parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    try {
      return format(parseISO(dateString), 'dd/MM/yyyy');
    } catch (error) {
      return dateString;
    }
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return 'Not specified';
    try {
      return format(parseISO(dateTimeString), 'dd/MM/yyyy HH:mm:ss');
    } catch (error) {
      return dateTimeString;
    }
  };

  if (!chequeData) {
    return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="body2" color="text.secondary">
            Cheque details have not been entered yet.
          </Typography>
          
          {canEdit && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditIcon />}
              onClick={handleEditStart}
            >
              Enter Details
            </Button>
          )}
        </Box>
        
        {isEditing && (
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Enter Cheque Details
              </Typography>
              <Box display="flex" gap={1}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<SaveIcon />}
                  onClick={handleEditSave}
                >
                  Save
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CancelIcon />}
                  onClick={handleEditCancel}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
            
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'grey.300' }}>
              <Table size="small">
                <TableBody>
                  {/* Bank Name */}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.50' }}>
                      <BankIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} color="primary" />
                      Bank Name *
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={editData.bank_name}
                        onChange={(e) => handleInputChange('bank_name', e.target.value)}
                        placeholder="Enter bank name"
                        required
                      />
                    </TableCell>
                  </TableRow>

                  {/* Bank Account No */}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.50' }}>
                      <NumbersIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} color="primary" />
                      Bank Acc. Number *
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        value={editData.bank_account_number}
                        onWheel={e =>
                          e.target instanceof HTMLElement && e.target.blur()
                        }
                        onChange={(e) => handleInputChange('bank_account_number', e.target.value)}
                        placeholder="Enter bank account number"
                        required
                      />
                    </TableCell>
                  </TableRow>
                  
                  {/* Cheque Number */}
                  <TableRow>
                    <TableCell sx={{ width: '30%', fontWeight: 'bold', backgroundColor: 'grey.50' }}>
                      <ChequeIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} color="primary" />
                      Cheque Number *
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        value={editData.cheque_number}
                        onWheel={e =>
                          e.target instanceof HTMLElement && e.target.blur()
                        }
                        onChange={(e) => handleInputChange('cheque_number', e.target.value)}
                        placeholder="Enter cheque number"
                        required
                      />
                    </TableCell>
                  </TableRow>

                  {/* Payee Name */}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.50' }}>
                      <PersonIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} color="primary" />
                      Payee Name *
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={editData.payee_name}
                        onChange={(e) => handleInputChange('payee_name', e.target.value)}
                        placeholder="Enter payee name"
                        required
                      />
                    </TableCell>
                  </TableRow>

                  {/* Amount */}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.50' }}>
                      <AmountIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} color="primary" />
                      Amount *
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        value={editData.cheque_amount}
                        onWheel={e =>
                          e.target instanceof HTMLElement && e.target.blur()
                        }
                        onChange={(e) => handleInputChange('cheque_amount', e.target.value)}
                        placeholder="Enter amount"
                        required
                        InputProps={{
                          startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>
                        }}
                      />
                    </TableCell>
                  </TableRow>

                  {/* Cheque Date */}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.50' }}>
                      <CalendarIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} color="primary" />
                      Cheque Date *
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        type="date"
                        value={editData.cheque_date}
                        onChange={(e) => handleInputChange('cheque_date', e.target.value)}
                        required
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </TableCell>
                  </TableRow>

                  {/* Remarks (if any) */}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.50' }}>
                      <TextSnippetIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} color="primary" />
                      Remarks (if any)
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={editData.remarks}
                        onChange={(e) => handleInputChange('remarks', e.target.value)}
                        placeholder="Enter Remarks if any"
                        required
                      />
                    </TableCell>
                  </TableRow>

                  {/* Cheque Image */}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.50' }}>
                      <ImageIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} color="primary" />
                      Cheque Image *
                    </TableCell>
                    <TableCell>
                      <Box>
                        <input
                          accept="image/*"
                          style={{ display: 'none' }}
                          id="cheque-image-upload"
                          type="file"
                          onChange={handleImageUpload}
                        />
                        <label htmlFor="cheque-image-upload">
                          <Button
                            variant="outlined"
                            component="span"
                            size="small"
                            startIcon={<ImageIcon />}
                            sx={{ mb: 1 }}
                          >
                            {selectedImage ? 'Change Image' : 'Select Image'}
                          </Button>
                        </label>
                        
                        {imagePreview && (
                          <Box mt={1}>
                            <img
                              src={imagePreview}
                              alt="Cheque preview"
                              style={{
                                maxWidth: '200px',
                                maxHeight: '100px',
                                objectFit: 'contain',
                                border: '1px solid #ddd',
                                borderRadius: '4px'
                              }}
                            />
                          </Box>
                        )}
                        
                        <Typography variant="caption" color="text.secondary" display="block">
                          Supported: JPG, PNG (Max: 1MB)
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box>
      {canEdit && !isEditing && (
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditIcon />}
            onClick={handleEditStart}
          >
            Edit Details
          </Button>
        </Box>
      )}

      {isEditing && (
        <Box display="flex" justifyContent="flex-end" gap={1} mb={2}>
          <Button
            variant="contained"
            size="small"
            startIcon={<SaveIcon />}
            onClick={handleEditSave}
          >
            Save
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<CancelIcon />}
            onClick={handleEditCancel}
          >
            Cancel
          </Button>
        </Box>
      )}

      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'grey.300' }}>
        <Table size="small">
          <TableBody>
            
            {/* Bank Name */}
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.50' }}>
                <BankIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} color="primary" />
                Bank Name
              </TableCell>
              <TableCell>
                {isEditing ? (
                  <TextField
                    fullWidth
                    size="small"
                    value={editData.bank_name}
                    onChange={(e) => handleInputChange('bank_name', e.target.value)}
                    placeholder="Enter bank name"
                  />
                ) : (
                  <Typography variant="body2">
                    {chequeData.bank_name || 'Not specified'}
                  </Typography>
                )}
              </TableCell>
            </TableRow>

            {/* Bank Account No */}
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.50' }}>
                <NumbersIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} color="primary" />
                Bank Acc. Number
              </TableCell>
              <TableCell>
                {isEditing ? (
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={editData.bank_account_number}
                    onWheel={e =>
                      e.target instanceof HTMLElement && e.target.blur()
                    }
                    onChange={(e) => handleInputChange('bank_account_number', e.target.value)}
                    placeholder="Enter bank account no"
                  />
                ) : (
                  <Typography variant="body2">
                    {chequeData.bank_account_number || 'Not specified'}
                  </Typography>
                )}
              </TableCell>
            </TableRow>

            {/* Cheque Number */}
            <TableRow>
              <TableCell sx={{ width: '30%', fontWeight: 'bold', backgroundColor: 'grey.50' }}>
                <ChequeIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} color="primary" />
                Cheque Number
              </TableCell>
              <TableCell>
                {isEditing ? (
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={editData.cheque_number}
                    onWheel={e =>
                      e.target instanceof HTMLElement && e.target.blur()
                    }
                    onChange={(e) => handleInputChange('cheque_number', e.target.value)}
                    placeholder="Enter cheque number"
                  />
                ) : (
                  <Typography variant="body2" fontWeight="medium">
                    {chequeData.cheque_number || 'Not specified'}
                  </Typography>
                )}
              </TableCell>
            </TableRow>

            {/* Payee Name */}
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.50' }}>
                <PersonIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} color="primary" />
                Payee Name
              </TableCell>
              <TableCell>
                {isEditing ? (
                  <TextField
                    fullWidth
                    size="small"
                    value={editData.payee_name}
                    onChange={(e) => handleInputChange('payee_name', e.target.value)}
                    placeholder="Enter payee name"
                  />
                ) : (
                  <Typography variant="body2">
                    {chequeData.payee_name || 'Not specified'}
                  </Typography>
                )}
              </TableCell>
            </TableRow>

            {/* Amount */}
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.50' }}>
                <AmountIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} color="primary" />
                Amount
              </TableCell>
              <TableCell>
                {isEditing ? (
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={editData.cheque_amount}
                    onWheel={e =>
                      e.target instanceof HTMLElement && e.target.blur()
                    }
                    onChange={(e) => handleInputChange('cheque_amount', e.target.value)}
                    placeholder="Enter amount"
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>
                    }}
                  />
                ) : (
                  <Typography variant="body2" fontWeight="medium" color="primary">
                    {formatAmount(chequeData.cheque_amount)}
                  </Typography>
                )}
              </TableCell>
            </TableRow>

            {/* Cheque Date */}
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.50' }}>
                <CalendarIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} color="primary" />
                Cheque Date
              </TableCell>
              <TableCell>
                {isEditing ? (
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    value={editData.cheque_date}
                    onChange={(e) => handleInputChange('cheque_date', e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                ) : (
                  <Typography variant="body2">
                    {formatDate(chequeData.cheque_date)}
                  </Typography>
                )}
              </TableCell>
            </TableRow>

            {/* Remarks (if any) */}
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.50' }}>
                <TextSnippetIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} color="primary" />
                Remarks
              </TableCell>
              <TableCell>
                {isEditing ? (
                  <TextField
                    fullWidth
                    size="small"
                    value={editData.remarks}
                    onChange={(e) => handleInputChange('remarks', e.target.value)}
                    placeholder="Enter remarks if any"
                  />
                ) : (
                  <Typography variant="body2">
                    {chequeData.remarks || 'Not specified'}
                  </Typography>
                )}
              </TableCell>
            </TableRow>

            {/* Cheque Image */}
            {chequeData.cheque_image_path && (
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.50' }}>
                  <ImageIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} color="primary" />
                  Cheque Image
                </TableCell>
                <TableCell>
                  {isEditing ? (
                    <Box>
                      {/* Current Image */}
                      <ImageViewer
                        imagePath={chequeData.cheque_image_path}
                        alt={`Cheque ${chequeData.cheque_number || 'Image'}`}
                        title="Current Cheque Image"
                        description={chequeData.cheque_number ? `Cheque No: ${chequeData.cheque_number}${chequeData.bank_name ? ` | ${chequeData.bank_name}` : ''}` : 'Current Cheque Document'}
                        downloadFilename={`cheque-${chequeData.cheque_number || 'image'}.jpg`}
                        thumbnailWidth="200px"
                        thumbnailHeight="120px"
                      />
                      
                      {/* Image Update Section */}
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Update Cheque Image
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                          <input
                            accept="image/*"
                            style={{ display: 'none' }}
                            id="update-cheque-image-upload"
                            type="file"
                            onChange={handleImageUpload}
                          />
                          <label htmlFor="update-cheque-image-upload">
                            <Button
                              variant="outlined"
                              component="span"
                              size="small"
                              startIcon={<ImageIcon />}
                            >
                              {selectedImage ? 'Change Image' : 'Select New Image'}
                            </Button>
                          </label>
                          
                          {selectedImage && (
                            <Typography variant="caption" color="success.main">
                              New image selected: {selectedImage.name}
                            </Typography>
                          )}
                        </Box>
                        
                        {imagePreview && (
                          <Box mt={1}>
                            <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                              Preview of new image:
                            </Typography>
                            <img
                              src={imagePreview}
                              alt="New cheque preview"
                              style={{
                                maxWidth: '200px',
                                maxHeight: '100px',
                                objectFit: 'contain',
                                border: '1px solid #ddd',
                                borderRadius: '4px'
                              }}
                            />
                          </Box>
                        )}
                        
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                          Supported: JPG, PNG (Max: 1MB). Note: New image will be saved when you save the cheque details.
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <ImageViewer
                      imagePath={chequeData.cheque_image_path}
                      alt={`Cheque ${chequeData.cheque_number || 'Image'}`}
                      title="Cheque Image"
                      description={chequeData.cheque_number ? `Cheque No: ${chequeData.cheque_number}${chequeData.bank_name ? ` | ${chequeData.bank_name}` : ''}` : 'Cheque Document'}
                      downloadFilename={`cheque-${chequeData.cheque_number || 'image'}.jpg`}
                      thumbnailWidth="200px"
                      thumbnailHeight="120px"
                    />
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ChequeDetails;
